import React, { useContext, Suspense } from 'react';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import LazyContext from '../LazyLoadingImage/LazyContext';
import SidebarBlock from './SidebarBlock';
import LoaderBlock from './_partials/LoaderBlock';
import cn from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import RWait from '@wix/da-react-app/pkg/client/ResourceCache/RWait';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { useSelector } from 'react-redux';
import { getIsDuperbrowseActive } from '../Duperbrowse/redux/selectors';
import s from './Sidebar.scss';
/**
 * Returns `true` if the supplied {@link block} is one we've provided an alternative placement for on mobile
 */ export function shouldRelocateBlockOnMobile(block) {
    // Monetization promos that aren't the dreamup one have a higher placement on mobile
    if (block.contentType.startsWith('monetization_promotion') && block.contentType !== 'monetization_promotion_dreamup_thirdparty' && block.contentType !== 'monetization_promotion_dreamup_firstparty') {
        return true;
    }
    // adoptable_info and pcp_info have a higher placement on mobile
    if ([
        'adoptable_info',
        'pcp_info'
    ].includes(block.contentType)) {
        return true;
    }
    return false;
}
function shouldRenderSidebarBlock(block, isDesktop) {
    return isDesktop ? true : !shouldRelocateBlockOnMobile(block);
}
const Sidebar = ({ deviation, className })=>{
    var _deviation_extended;
    const isDuperbrowse = useSelector(getIsDuperbrowseActive);
    const isR18Async = useFlag('seo_r18_async');
    const isDesktop = !useContext(MobileContext);
    const blocks = deviation === null || deviation === void 0 ? void 0 : (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.relatedContent;
    const isLoading = !deviation.extended;
    // Related is ready when we get the blocks OR in duperbrowse where we dont lazy
    // fetch the related blocks meaning it comes with extended
    const relatedIsReady = !isLoading && (blocks || isDuperbrowse);
    const isMounted = useIsMounted();
    const loadingSections = /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(LoaderBlock, {
        index: 0
    }), /*#__PURE__*/ React.createElement(LoaderBlock, {
        index: 1
    }), /*#__PURE__*/ React.createElement(LoaderBlock, {
        index: 2
    }));
    if (isR18Async) {
        return /*#__PURE__*/ React.createElement(ErrorBoundary, {
            debugComponent: "Sidebar"
        }, /*#__PURE__*/ React.createElement("div", {
            className: cn(s['root'], className),
            role: "complementary"
        }, isMounted && /*#__PURE__*/ React.createElement(Suspense, {
            fallback: loadingSections
        }, /*#__PURE__*/ React.createElement(RWait, {
            key: "relatedContent",
            cacheName: "relatedContent",
            skipCache: !!relatedIsReady
        }), relatedIsReady && blocks && blocks.map((block, index)=>shouldRenderSidebarBlock(block, isDesktop) && /*#__PURE__*/ React.createElement(LazyContext.Provider, {
                value: isDesktop ? index > 1 : true,
                key: block.contentType
            }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
                value: {
                    _rankOffset: 0
                }
            }, /*#__PURE__*/ React.createElement(SidebarBlock, {
                block: block,
                deviation: deviation
            })))), !relatedIsReady && /*#__PURE__*/ React.createElement(React.Fragment, null, loadingSections)), !isMounted && /*#__PURE__*/ React.createElement(React.Fragment, null, loadingSections)));
    }
    return /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "Sidebar"
    }, /*#__PURE__*/ React.createElement("div", {
        className: cn(s['root'], className),
        role: "complementary"
    }, !isLoading && blocks && blocks.map((block, index)=>shouldRenderSidebarBlock(block, isDesktop) && /*#__PURE__*/ React.createElement(LazyContext.Provider, {
            value: isDesktop ? index > 1 : true,
            key: block.contentType
        }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
            value: {
                _rankOffset: 0
            }
        }, /*#__PURE__*/ React.createElement(SidebarBlock, {
            block: block,
            deviation: deviation
        })))), isLoading && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(LoaderBlock, {
        index: 0
    }), /*#__PURE__*/ React.createElement(LoaderBlock, {
        index: 1
    }), /*#__PURE__*/ React.createElement(LoaderBlock, {
        index: 2
    }))));
};
Sidebar.displayName = 'Sidebar';
export default Sidebar;
